import { useRef } from 'react';
import { Link } from 'react-router';
import { LogOut, Moon, Settings, Sun, Upload } from 'lucide-react';

import { useLogout, useSession } from '@/features/auth';
import { getRoleLabel } from '@/features/users';
import { useShellPreferences } from '@/shared/shell/ShellPreferencesContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shared/ui/tooltip';

const topbarButtonClass =
  'inline-flex h-9 w-9 items-center justify-center rounded-lg border border-control bg-control text-control shadow-resting transition-colors duration-150 hover:border-control-hover hover:text-link focus-visible:focus-ring';

const menuIconClass = 'h-4 w-4 text-placeholder in-data-highlighted:text-current';

function userInitials(user) {
  const name = user?.name || user?.email || 'U';
  return (
    name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join('') || 'U'
  );
}

export default function AppTopbar() {
  const { user } = useSession();
  const logout = useLogout();
  const { theme, toggleTheme, avatarUrl, setAvatarUrl } = useShellPreferences();
  const uploadInputRef = useRef(null);
  const themeLabel = theme === 'dark' ? 'Attiva tema chiaro' : 'Attiva tema scuro';

  const handleAvatarUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return;

    const reader = new FileReader();
    reader.onload = () => setAvatarUrl(reader.result);
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex items-center gap-1.5 sm:gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={toggleTheme}
              aria-label={themeLabel}
              className={topbarButtonClass}
            >
              {theme === 'dark' ? (
                <Sun className="h-4 w-4" aria-hidden />
              ) : (
                <Moon className="h-4 w-4" aria-hidden />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent>{themeLabel}</TooltipContent>
        </Tooltip>

        <DropdownMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger
                aria-label="Profilo utente"
                className="rounded-full ring-2 ring-transparent transition-shadow duration-150 hover:ring-control-hover focus-visible:focus-ring aria-expanded:ring-control-hover"
              >
                <Avatar className="h-9 w-9">
                  {avatarUrl ? (
                    <AvatarImage src={avatarUrl} alt={user?.name || 'Profilo'} />
                  ) : (
                    <AvatarFallback>{userInitials(user)}</AvatarFallback>
                  )}
                </Avatar>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent>Profilo utente</TooltipContent>
          </Tooltip>
          <DropdownMenuContent align="end" sideOffset={6} className="w-64">
            <DropdownMenuLabel>
              <span className="block truncate text-card-title text-strong">
                {user?.name || 'Utente'}
              </span>
              <span className="block truncate text-caption text-subtle">{user?.email}</span>
              <span className="mt-1 block text-caption text-muted">{getRoleLabel(user?.role)}</span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => uploadInputRef.current.click()}>
              <Upload className={menuIconClass} aria-hidden />
              Carica foto profilo
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/settings">
                <Settings className={menuIconClass} aria-hidden />
                Impostazioni
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={logout}>
              <LogOut className={menuIconClass} aria-hidden />
              Esci
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        {/* Fuori dal menu: il menu si smonta alla scelta, prima che arrivi il file. */}
        <input
          ref={uploadInputRef}
          type="file"
          accept="image/*"
          aria-label="Carica foto profilo"
          className="hidden"
          onChange={handleAvatarUpload}
        />
      </div>
    </TooltipProvider>
  );
}
